import { PDFDocument } from "pdf-lib";
import { showToast } from "../../components/ui/custom-toast";
import { getErrorMessage } from "../../../api/errorMap";
import { useAuthStore, type AuthUser } from "../../../stores/authStore";
import type {
  GovernanceDocumentSummaryResponse,
  SupportingEvidence,
  HistoryEvent,
} from "../../../api/frameworksApi";
import { DOCUMENT_TYPES } from "./constants";
import type { GovDocRow, EvidenceRow } from "./types";

/* ── Authenticated download helper ── */
export async function triggerDownload(url: string, fileName?: string | null) {
  const token = useAuthStore.getState().getToken();
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // The success path streams a file blob; error responses come back as the
      // standard JSON error envelope. Surface the real API message (e.g. a
      // permission-denied detail) instead of a generic "Download failed".
      let parsed: unknown = null;
      try {
        parsed = await res.json();
      } catch {
        /* non-JSON error body — fall back to the generic message */
      }
      const apiErr = (
        parsed as { errors?: { title?: string; detail?: string; code?: string }[] }
      )?.errors?.[0];
      // Downloading a file requires the 'export' permission. When the user
      // lacks it the API replies 403 (identity code IDN-0003-E, title
      // "Permission Denied"). Show that explicitly rather than a generic
      // "Download failed", and derive it from the status too so it still
      // shows when the error body can't be parsed.
      const isPermissionDenied =
        res.status === 403 ||
        apiErr?.code === "IDN-0003-E" ||
        apiErr?.title === "Permission Denied";
      if (isPermissionDenied) {
        showToast(
          "warning",
          apiErr?.detail ||
            "You don't have permission to download this file. Export permission is required.",
          "Permission Denied",
        );
        return;
      }
      const title = apiErr?.title || "Download failed";
      showToast("error", getErrorMessage(parsed, "Download failed"), title);
      return;
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const serverName =
      disposition
        .match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)?.[1]
        ?.trim() ?? "";
    const name = fileName || serverName || "download";
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Download failed";
    showToast("error", msg || "Download failed");
  }
}

/** Map backend lifecycle status to StatusBadge variant */
export function mapLifecycleToVariant(
  s: string,
):
  | "draft"
  | "in_review"
  | "approved"
  | "published"
  | "review_overdue"
  | "retired" {
  const map: Record<
    string,
    | "draft"
    | "in_review"
    | "approved"
    | "published"
    | "review_overdue"
    | "retired"
  > = {
    DRAFT: "draft",
    IN_REVIEW: "in_review",
    APPROVED: "approved",
    PUBLISHED: "published",
    REVIEW_OVERDUE: "review_overdue",
    RETIRED: "retired",
  };
  return map[s] ?? "draft";
}

export function formatDate(d: string | null): string {
  if (!d) return "-";
  return d.split("T")[0].split("-").reverse().join("/");
}

export function formatDateTime(d: string | null): string {
  if (!d) return "-";
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${day}/${month}/${year} ${hour12}:${minutes} ${ampm}`;
}

export function formatDocType(t: string): string {
  return DOCUMENT_TYPES.find((d) => d.value === t)?.label ?? t;
}

/**
 * Validate a title field. Returns an error message, or null when valid.
 * Rejects junk such as a single character repeated (e.g. "HHHHHH").
 */
export function getTitleValidationError(raw: string): string | null {
  const title = raw.trim();
  if (!title) return null; // emptiness is handled by the required/disabled state
  if (title.length < 3) return "Title must be at least 3 characters.";
  if (!/[a-zA-Z0-9]/.test(title))
    return "Title must contain letters or numbers.";
  // Distinct non-space characters — a single repeated character has only 1.
  const distinct = new Set(title.replace(/\s+/g, "").toLowerCase()).size;
  if (distinct < 2)
    return "Enter a meaningful title, not a single repeated character.";
  return null;
}

export function toGovDocRow(d: GovernanceDocumentSummaryResponse): GovDocRow {
  return {
    id: d.id,
    title: d.title,
    documentType: d.documentType,
    status: d.status,
    currentVersion: d.currentVersion,
    versionCount: d.versionCount,
    reviewDate: formatDate(d.reviewDate),
    createdAt: formatDate(d.createdAt),
    updatedAt: formatDate(d.updatedAt),
    daysUntilReview: d.daysUntilReview,
    reviewDueSoon: d.reviewDueSoon ?? false,
    hasFile: d.hasFile,
  };
}

export function toEvidenceRow(d: SupportingEvidence): EvidenceRow {
  return {
    id: d.id,
    title: d.title,
    description: "",
    tags: d.tags,
    hasFile: d.hasFile,
    fileName: d.fileName,
    fileSize: d.fileSize,
    contentType: d.contentType,
    createdAt: formatDate(d.createdAt),
    updatedAt: formatDate(d.updatedAt),
  };
}

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getAuthUserDisplayName(user: AuthUser | null): string | null {
  if (!user) return null;
  return (
    user.displayName?.trim() ||
    user.username?.trim() ||
    user.email?.trim() ||
    null
  );
}

export function getHistoryActorName(
  event: HistoryEvent,
  currentUser: AuthUser | null,
): string {
  const actorName = event.performedByName?.trim();
  const currentUserName = getAuthUserDisplayName(currentUser);
  const actorId = event.performedBy?.trim();
  const currentUserIds = [
    currentUser?.userId,
    currentUser?.username,
    currentUser?.email,
  ].filter(Boolean);
  const matchesCurrentUser = currentUserIds.some((id) => id === actorId);
  const userDrivenEvents = new Set([
    "CREATED",
    "UPDATED",
    "SUBMITTED",
    "APPROVED",
    "REJECTED",
    "PUBLISHED",
    "RETIRED",
    "VERSION_CREATED",
  ]);

  if (
    currentUserName &&
    (matchesCurrentUser ||
      (!actorName && userDrivenEvents.has(event.eventType)) ||
      (actorName?.toLowerCase() === "system" &&
        userDrivenEvents.has(event.eventType)))
  ) {
    return currentUserName;
  }

  return actorName || "System";
}

/* ── Helper: lifecycle action label ── */
export function lifecycleActionLabel(action: string): string {
  const labels: Record<string, string> = {
    submit: "Submit for Review",
    approve: "Approve",
    publish: "Publish",
    reject: "Reject",
    "new-version": "Add New Version",
    retire: "Withdraw",
  };
  return labels[action] ?? action;
}

/**
 * Returns a copy of the PDF blob with its /Title metadata set to `fileName`,
 * so the browser's built-in PDF viewer displays the real name instead of
 * "(anonymous)". Falls back to the original blob if anything goes wrong.
 */
export async function withPdfTitle(
  blob: Blob,
  fileName: string | null,
): Promise<Blob> {
  if (!fileName) return blob;
  // Drop the extension for a cleaner viewer title (e.g. "Report" not "Report.pdf").
  const title = fileName.replace(/\.[^./\\]+$/, "") || fileName;
  try {
    const bytes = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(bytes, { updateMetadata: false });
    pdf.setTitle(title, { showInWindowTitleBar: true });
    const out = await pdf.save();
    // `out` is a Uint8Array — a valid BlobPart at runtime; cast for TS's
    // stricter ArrayBuffer-generic typing.
    return new Blob([out as BlobPart], { type: "application/pdf" });
  } catch {
    return blob;
  }
}
